<template>
  <banners
    :sticky-top="true"
    :narrow="true"
  >
    <Layout
      :title="title"
      :loads="loads"
      :isDesktop="isDesktop"
      :isLoading="isLoading"
      :isLoadsRefetching="isLoadsRefetching"
      @search-loads="searchLoads"
      @fetch-more="fetchMoreLoads"
      @select-page="selectPage"
    >
    </Layout>
  </banners>
</template>

<script>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Banners from '@/components/Banners.vue';
import Layout from '@/components/loads/Layout.vue';
import { loadQuery, createFilterQuery } from '../../utils/loadFilters.js';
import { useScreenSize } from '@/composables/useScreenSize';

export default {
  name: 'Load Dashboard',
  components: {
    Banners,
    Layout,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { screenSize } = useScreenSize();

    const isLoadsRefetching = ref(false);

    const isLoading = computed(() => store.state.loads?.isLoadsLoading);

    const isDesktop = computed(() => screenSize.value === 'desktop');

    const title = computed(() =>
      isDesktop.value ? 'Search for Loads' : 'Loads for you'
    );

    const currentPage = computed(
      () => store.state.loads?.dispatch_pagination?.currentPage
    );

    const totalPages = computed(
      () => store.state.loads?.dispatch_pagination?.totalPages
    );

    const getLoads = async ({ query = '', refetch = false, page = 1 }) => {
      const sort = router.currentRoute?.value?.query?.sort || '';
      try {
        // TODO -- update request
        await store.dispatch('loads/searchLoads', {
          query,
          sort,
          refetch,
          page,
          // status: currentTab.value,
        });
      } catch (error) {
        console.error('Error fetching loads: ', error);
      } finally {
        store.commit('loads/setLoading', false);
      }
    };

    const addFiltersFromUrl = (values) => {
      let sort = 'start_stop_scheduled_window_started_at+asc';

      getLoads({ sort, query: values });
    };

    const searchFilters = computed(() => store.state.loads?.loadSearchFilters);

    const initialLoadSeach = ref(false);

    onBeforeMount(async () => {
      store.commit('loads/setLoading', true);

      const existingQuery = router.currentRoute?.value?.query;
      const existingSort = existingQuery?.sort || '';

      const loadSearchFilterStorage =
        JSON.parse(localStorage.getItem('load_search_filters')) || {};
      // const existingFilters = Object.entries(searchFilters?.value)
      //   ? searchFilters?.value
      //   : loadSearchFilterStorage;

      if (Object.entries(searchFilters?.value)) {
        await store.commit(
          'loads/setLoadSearchFilters',
          loadSearchFilterStorage
        );
      }

      const combinedQuery = createFilterQuery(
        loadSearchFilterStorage,
        existingQuery
      );

      if (Object.entries(combinedQuery)?.length > 0) {
        initialLoadSeach.value = true;
      }

      if (
        Object.entries(store.state.loads?.loadSearchFilters)?.length > 0 &&
        store.state.loads?.loads?.length > 0
      ) {
        // Already has load index data, and filters haven't changed
        store.commit('loads/setLoading', false);
      } else {
        await store.dispatch('loads/searchLoads', {
          query: loadSearchFilterStorage,
          sort: existingSort,
        });
      }

      await router.push({
        query: {
          ...combinedQuery,
        },
      });

      setTimeout(() => {
        // Fallback
        initialLoadSeach.value = false;
      }, 1500);
    });

    watch(
      () => router.currentRoute.value.query,
      (newValue) => {
        if (initialLoadSeach.value) {
          initialLoadSeach.value = false;
        } else {
          let queries = { ...newValue };
          if (newValue?.equipment && !Array.isArray(newValue?.equipment)) {
            queries.equipment = [newValue.equipment];
          } else if (newValue?.trailer && !Array.isArray(newValue?.trailer)) {
            queries.trailer = [newValue.trailer];
          }

          if (router.currentRoute.value?.name === 'Load Board') {
            addFiltersFromUrl(searchFilters.value);
          }
        }
      },
      { deep: true }
    );

    const loads = computed(() => store.state?.loads?.loads);

    const searchLoads = async (term) => {
      await store.dispatch('loads/getLoads', {
        // query: `${loadQuery[currentTab.value]}&q[single_external_load_number_equals]=${term}`,
        query: `${loadQuery['dispatch']}&q[single_external_load_number_equals]=${term}`,
        // status: currentTab.value,
      });
    };

    const fetchMoreLoads = async () => {
      if (!isLoading.value && !isDesktop?.value) {
        console.log('Hit infinite scroll');
        if (currentPage?.value !== totalPages?.value) {
          isLoadsRefetching.value = true;

          // re-fetch loads based on page. Add to existing array
          await getLoads({
            page: +currentPage.value + 1,
            refetch: true,
          });

          isLoadsRefetching.value = false;
        }
      }
    };

    const selectPage = async (page) => {
      if (page != currentPage?.value) {
        await getLoads({
          page,
        });
      }
    };

    return {
      loads,
      title,
      isDesktop,
      isLoading,
      isLoadsRefetching,
      searchLoads,
      fetchMoreLoads,
      selectPage,
      initialLoadSeach,
    };
  },
};
</script>
